import { posthogEvents, relewiseTags } from '@/shared/constants/constants';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';
import { parseResultsCount } from '@/shared/tracking/parse-results-count';
import type {
  SearchEngine,
  SearchSurface
} from '@/shared/tracking/search-used';
import { deepQuerySelectorAll } from '@/shared/tracking/shadow-dom';

type SearchResultClickedData = {
  engine: SearchEngine;
  surface: SearchSurface;
  query: string;
  // 1-based display position. Omitted (never 0) when the position could not be
  // resolved — 0 would corrupt position-based funnel analysis.
  position?: number;
  product_id: string;
  product_sku: string;
  product_url: string;
  product_title: string;
  results_count: number | null;
};

const getSearchTerm = (override?: string | null): string => {
  if (override != null && override !== '') {
    return override;
  }
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('rw-term') || queryParams.get('q') || '';
};

const emit = (
  data: Omit<SearchResultClickedData, 'position'> & { position: number }
) => {
  const { position, ...rest } = data;
  const payload: SearchResultClickedData =
    Number.isFinite(position) && position > 0 ? { ...rest, position } : rest;

  posthogLog('search_result_clicked', payload);
  window.posthog?.capture(posthogEvents.SEARCH_RESULT_CLICKED, payload);
};

export const initNativeSearchResultClickTracking = (
  resultsCountElement: HTMLElement | null
) => {
  const section = document.querySelector<HTMLElement>(
    '.js-main-search-section'
  );
  if (!section) return;

  section.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest<HTMLElement>('.js-search-result-card');
    if (!card || !section.contains(card)) return;

    const cards = Array.from(
      section.querySelectorAll<HTMLElement>('.js-search-result-card')
    );
    const position = cards.indexOf(card) + 1;

    const anchor = card.querySelector<HTMLAnchorElement>('a[href]');

    emit({
      engine: 'native',
      surface: 'results_page',
      query: getSearchTerm(),
      position,
      product_id: card.dataset.productId || '',
      product_sku: card.dataset.productSku || '',
      product_url: anchor?.href || '',
      product_title: card.dataset.productTitle || '',
      results_count: parseResultsCount(resultsCountElement)
    });
  });
};

// Delegates from `root` (document.body) rather than the scroll container because
// the container lives behind `v-if="isPanelOpen"` and is absent at attach time.
export const PREDICTIVE_SCROLL_CONTAINER_SELECTOR =
  '.js-header-search-scroll-container';

export const initNativePredictiveResultClickTracking = (
  root: HTMLElement,
  getTerm: () => string | null
): (() => void) => {
  const handler = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const result = target.closest<HTMLAnchorElement>('.js-predictive-result');
    // No root.contains(result) check: the dropdown detaches the clicked result before
    // the click bubbles here, so containment would drop valid clicks. `result` comes
    // from the click target's own ancestry, so it stays valid when detached.
    if (!result) return;

    const scope =
      result.closest<HTMLElement>(PREDICTIVE_SCROLL_CONTAINER_SELECTOR) ?? root;

    const results = Array.from(
      scope.querySelectorAll<HTMLElement>('.js-predictive-result')
    );
    const position = results.indexOf(result) + 1;

    emit({
      engine: 'native',
      surface: 'predictive',
      query: getSearchTerm(getTerm()),
      position,
      product_id: result.dataset.productId || '',
      product_sku: result.dataset.productSku || '',
      product_url: result.href || '',
      product_title: result.dataset.productTitle || '',
      results_count: results.length
    });
  };

  root.addEventListener('click', handler);
  return () => root.removeEventListener('click', handler);
};

// Narrow local shape — the full type lives in @relewise/client, loaded at runtime
// via the external bundle and not available as a build-time dependency.
export type RelewiseProductResult = {
  productId?: string | null;
  displayName?: string | null;
  rank?: number;
  variant?: { variantId?: string | null } | null;
  data?: Record<string, { value?: string | null } | undefined>;
};

type RelewisePositionCount = {
  // null means position could not be resolved; caller should fall back to DOM.
  position: number | null;
  resultsCount: number | null;
};

const RELEWISE_RESULT_TAGS = [
  relewiseTags.PRODUCT_TILE_TAG,
  relewiseTags.OVERLAY_PRODUCT_TAG
] as const;

// Keyed off the result element, not an anchor: the tile template renders
// <a class="rw-tile"> only when the product has a Url; without Url it renders
// <div class="rw-tile">, so an anchor-gated handler would silently miss those results.
const initRelewiseClickCapture = (
  root: HTMLElement,
  surface: SearchSurface,
  resolvePositionCount: (
    clickedProduct: RelewiseProductResult | undefined
  ) => RelewisePositionCount | null,
  getResultTilesFallback: () => HTMLElement[],
  getTerm: () => string | null
) => {
  root.addEventListener('click', (event) => {
    const path = event.composedPath();

    const tile = path.find(
      (node): node is HTMLElement & { product?: RelewiseProductResult } =>
        node instanceof HTMLElement &&
        (RELEWISE_RESULT_TAGS.includes(
          node.tagName.toLowerCase() as (typeof RELEWISE_RESULT_TAGS)[number]
        ) ||
          Boolean((node as { product?: unknown }).product))
    );
    if (!tile) return;

    const product = tile.product;

    // We do NOT use product.rank — it is the relevance rank, not the display position.
    const resolved = resolvePositionCount(product);
    let position: number;
    let resultsCount: number | null;
    if (resolved?.position != null && resolved.position > 0) {
      position = resolved.position;
      resultsCount = resolved.resultsCount;
    } else {
      const tiles = getResultTilesFallback();
      position = tiles.indexOf(tile) + 1;
      // Prefer instance-state count over tile count: the grid may render tiles
      // twice (doubled), whereas hits/products.length is always accurate.
      resultsCount = resolved?.resultsCount ?? (tiles.length || null);
    }

    const productUrl =
      (product?.data && 'Url' in product.data && product.data.Url?.value) ||
      path.find(
        (node): node is HTMLAnchorElement =>
          node instanceof HTMLAnchorElement && Boolean(node.href)
      )?.href ||
      '';

    emit({
      engine: 'relewise',
      surface,
      query: getSearchTerm(getTerm()),
      position,
      product_id: product?.productId || '',
      product_sku: product?.variant?.variantId || '',
      product_url: productUrl,
      product_title: product?.displayName || '',
      results_count: resultsCount
    });
  });
};

type RelewiseProductSearchInstance = HTMLElement & {
  products?: RelewiseProductResult[];
  searchResult?: { hits?: number } | null;
};

export const resolveResultsPagePositionCount = (
  clickedProduct: RelewiseProductResult | undefined
): RelewisePositionCount | null => {
  const el = document.querySelector<RelewiseProductSearchInstance>(
    relewiseTags.SEARCH_TAG
  );
  const products = el?.products;
  if (!Array.isArray(products)) {
    const hits = el?.searchResult?.hits;
    return typeof hits === 'number'
      ? { position: null, resultsCount: hits }
      : null;
  }

  const hits = el?.searchResult?.hits;
  const resultsCount =
    typeof hits === 'number' ? hits : products.length || null;

  const clickedId = clickedProduct?.productId;
  const index =
    clickedId != null
      ? products.findIndex((p) => p?.productId === clickedId)
      : -1;

  return { position: index + 1, resultsCount };
};

// results_count uses the displayed (capped) set, not .productSearchResultHits,
// so it matches what search_used reports for the predictive surface.
type RelewiseOverlayResultEntry = { product?: RelewiseProductResult };
type RelewiseOverlayInstance = HTMLElement & {
  results?: RelewiseOverlayResultEntry[] | null;
};

// .results is a mixed array (redirects, predictions, products, categories, showAllResults);
// filter to entries that carry a .product to get the displayed product set.
export const getOverlayDisplayedProducts = (
  overlay: HTMLElement
): RelewiseProductResult[] | null => {
  const el = overlay as RelewiseOverlayInstance;
  const results = el.results;
  if (!Array.isArray(results)) {
    return null;
  }

  return results
    .map((entry) => entry?.product)
    .filter((p): p is RelewiseProductResult => Boolean(p));
};

export const resolveOverlayPositionCount = (
  overlay: HTMLElement,
  clickedProduct: RelewiseProductResult | undefined
): RelewisePositionCount | null => {
  const products = getOverlayDisplayedProducts(overlay);
  if (products == null) {
    return null;
  }

  const clickedId = clickedProduct?.productId;
  const index =
    clickedId != null
      ? products.findIndex((p) => p?.productId === clickedId)
      : -1;

  return { position: index + 1, resultsCount: products.length || null };
};

/** Result-tile tags, joined for a single deep-shadow query selector. */
const RELEWISE_RESULT_TAG_SELECTOR = RELEWISE_RESULT_TAGS.join(',');

export const initRelewiseSearchResultClickTracking = () => {
  const section = document.querySelector<HTMLElement>(
    '.js-relewise-search-section'
  );
  if (!section) return;

  initRelewiseClickCapture(
    section,
    'results_page',
    resolveResultsPagePositionCount,
    () => deepQuerySelectorAll(section, RELEWISE_RESULT_TAG_SELECTOR),
    () => null
  );
};

export const initRelewiseOverlayResultClickTracking = (
  overlay: HTMLElement,
  getTerm: () => string | null
) => {
  initRelewiseClickCapture(
    overlay,
    'predictive',
    (clickedProduct) => resolveOverlayPositionCount(overlay, clickedProduct),
    () => deepQuerySelectorAll(overlay, relewiseTags.OVERLAY_PRODUCT_TAG),
    getTerm
  );
};

export const initSearchResultClickTracking = (
  engine: SearchEngine,
  resultsCountElement: HTMLElement | null
) => {
  if (engine === 'relewise') {
    initRelewiseSearchResultClickTracking();
  } else {
    initNativeSearchResultClickTracking(resultsCountElement);
  }
};
